import clsx from 'clsx';
import { CheckIcon, ClockIcon, LoaderCircleIcon } from 'lucide-preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { connect } from 'react-redux';
import { buildURL } from 'Shared/fetch';
import useFetch from 'use-http';

import { Button } from '@/components/Buttons';

import getEmailVerificationPath from './getEmailVerificationPath';
import style from './style.scss';

/**
 * How long the resend button stays disabled after a send, in seconds.
 *
 * Front-end only: the server is not asked to enforce it, so this bounds the button and nothing
 * more. TODO: move to a server-side limit before relying on it for abuse control.
 */
export const RESEND_COOLDOWN_SECONDS = 60;

/** `0:45`, the countdown shape the design asks for, rather than a bare second count. */
const formatCooldown = (seconds) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

const VerificationCodeInput = ({
  props: {
    emailVerified,
    setEmailVerified,
    email: emailFromParentComp,
    isGiftCardUser,
    plan,
    price,
    // Offered only where there is somewhere to go back to. The standalone page has no earlier
    // step, so it passes nothing and the control does not render.
    onUseDifferentEmail,
  },
  emailFromGlobalsState,
}) => {
  const [code, setCode] = useState(['', '', '', '']);
  const [isDisabled, setIsDisabled] = useState(false);
  const inputRefs = useRef([]);
  const [infoMessage, setInfoMessage] = useState('');
  const [invalidCount, setInvalidCount] = useState(0);
  const [errorOccured, setErrorOccured] = useState(false);
  const [resendButtonClicked, setResendButtonClicked] = useState(false);

  const [emailResent, setEmailResent] = useState(false);
  const [emailResentError, setEmailResentError] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const { post: postVerify, loading: loadingverify } = useFetch(buildURL('/users/emailVerification'));
  const { post: postResend, loading: loadingResend } = useFetch(buildURL('/users/emailVerificationResend'));
  const email = emailFromParentComp ?? emailFromGlobalsState;
  const codeComplete = code.every((digit) => digit);
  const showCodeError = errorOccured && !emailVerified && !loadingverify;

  useEffect(() => {
    inputRefs.current[0].focus();
  }, []);

  // Tick the resend cooldown down to zero, then re-enable the button.
  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }
    const id = setTimeout(() => setResendCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  const validateTheCode = async (token) => {
    setErrorOccured(false);
    setResendButtonClicked(false);
    setEmailResent(false);
    setEmailResentError(false);

    try {
      const { valid, alreadyValidated, invalid, error } = await postVerify({
        token,
        email: email.toLowerCase(),
        source: 'page',
      });
      if (valid) {
        setInfoMessage('Success!');
        setEmailVerified(true);
        setIsDisabled(true);
      } else if (alreadyValidated) {
        setInfoMessage('Your email is already verified');
        setEmailVerified(true);
        setIsDisabled(true);
      } else if (invalid) {
        setErrorOccured(true);
        setEmailVerified(false);
        // The "try again" half of the old message now sits in the prompt above the boxes, which
        // swaps on error (Figma 8306-14846), so this only has to name what went wrong.
        setInfoMessage('Incorrect code');
        setInvalidCount(invalidCount + 1);
      } else if (error) {
        setErrorOccured(true);
        setInfoMessage('Incorrect code');
        setInvalidCount(invalidCount + 1);
      }

      if (invalidCount >= 6 && !emailVerified) {
        setInfoMessage('Having trouble? Try clicking the link in your email.');
      }
      if (invalidCount >= 10 && !emailVerified) {
        setInfoMessage('Still having trouble? Drop a line to support@zoolife.tv.');
      }
    } catch (err) {
      console.error('error :', err);
    }
  };

  const resendhandler = async () => {
    setResendButtonClicked(true);
    setEmailResent(false);
    setEmailResentError(false);
    const { emailSent, resendError } = await postResend({
      email,
      emailVerificationPath: getEmailVerificationPath({
        isGiftCardUser,
        plan,
        price,
      }),
    });

    if (emailSent) {
      setEmailResent(true);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } else if (resendError) {
      setEmailResentError(true);
      // No cooldown on failure — they could not have been sent anything to wait for.
      setResendButtonClicked(false);
    }
  };

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (Number.isInteger(parseInt(value, 10)) && value.length === 1) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);

      if (value && index < 3) {
        inputRefs.current[index + 1].focus();
      }
      if (newCode.every((digit) => digit)) {
        validateTheCode(newCode.join(''));
      }
    } else {
      // if value is longer than 1 character for any uncontrolled reson,
      // only use the first character
      const newCode = [...code];
      // eslint-disable-next-line prefer-destructuring
      newCode[index] = value.split('')[0];
      setCode(newCode);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text/plain');
    const pasteDigits = pasteData.split('').slice(0, 4);

    const newCode = [...code];
    pasteDigits.forEach((digit, index) => {
      newCode[index] = digit;
      inputRefs.current[index].value = digit;
    });
    setCode(newCode);
    if (pasteDigits.length === 4) {
      inputRefs.current[3].focus();
      validateTheCode(pasteDigits.join(''));
    } else {
      inputRefs.current[pasteDigits.length].focus();
    }
  };

  const handleFocus = (e) => {
    if (e.target.value !== '') {
      e.target.select();
    }
  };

  /**
   * The single status line under the boxes. Figma gives it one slot, so verifying, resending and
   * their outcomes take turns in it rather than stacking into two message blocks as before.
   *
   * Verification outranks resending: a code being checked is what the visitor is waiting on, and
   * `validateTheCode` clears the resend flags on the way in, so the two cannot both be live.
   */
  const status = (() => {
    if (loadingverify) {
      return { tone: 'progress', message: 'Validating code, please wait...' };
    }
    if (emailVerified) {
      return { tone: 'success', message: infoMessage };
    }
    if (errorOccured) {
      return { tone: 'error', message: infoMessage };
    }
    if (resendButtonClicked && loadingResend) {
      return { tone: 'progress', message: 'Resending email, please wait...' };
    }
    if (resendButtonClicked && emailResent) {
      return { tone: 'success', message: 'A new code was sent' };
    }
    if (emailResentError) {
      return {
        tone: 'error',
        message: 'An error has occurred. If the problem persists, please contact us support@zoolife.tv',
      };
    }
    return null;
  })();

  return (
    <>
      <div>
        {/*
          The weights carry `!` because `globalStyle.scss` ends `h1, h2, h3` with
          `font-weight: 700 !important`, which a plain utility cannot outrank.
        */}
        <h1 className="preflight preflight-h1 text-center text-2xl !font-semibold leading-8 text-[#162B1B]">
          Let’s Verify Your Email
        </h1>
        <p className="preflight preflight-p mt-3 text-center text-base leading-6 text-stone-500">
          We have sent an email to
          <br />
          {email}
        </p>
      </div>
      <div className="my-8 flex min-h-0 grow flex-col items-center justify-center gap-7">
        <h2 className="preflight preflight-h2 text-center text-base !font-normal leading-6 text-black">
          {showCodeError ? 'Check the code and try again' : 'Check your email and enter code below'}
        </h2>
        <div className="flex items-center gap-[7px]">
          {code.map((digit, index) => (
            <input
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              // eslint-disable-next-line no-return-assign
              ref={(element) => (inputRefs.current[index] = element)}
              type="number"
              min="0"
              max="9"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e, index)}
              onPaste={handlePaste}
              onFocus={(e) => handleFocus(e, index)}
              disabled={isDisabled}
              inputMode="numeric"
              className={clsx(
                style.codeInput,
                'preflight preflight-input',
                'size-[55px] rounded-lg border bg-white p-0 text-center text-[2rem] leading-none shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] outline-none',
                showCodeError
                  ? 'border-rose-700 text-rose-700 focus:border-rose-700'
                  : 'border-[#B8B8B8] text-primary-green focus:border-primary-green',
              )}
            />
          ))}
        </div>
        {status && (
          <div
            role="status"
            className={clsx(
              'flex items-center justify-center gap-[5px] text-center text-[13px] font-medium',
              status.tone === 'success' && 'rounded-[5px] bg-green-100 px-2.5 py-[5px] text-green-600',
              status.tone === 'error' && 'text-[#B02A37]',
              status.tone === 'progress' && 'text-stone-500',
            )}
          >
            {status.tone === 'success' && <CheckIcon className="size-5 shrink-0" />}
            {status.tone === 'progress' && <LoaderCircleIcon className="size-4 shrink-0 animate-spin" />}
            {status.message}
          </div>
        )}
      </div>
      <div className="flex w-full max-w-[279px] flex-col items-center gap-[21px] self-center">
        {/*
          Left enabled on an empty form, as Figma 8306-14454 draws it: greying it out is the one
          thing on the opening screen that would read as broken. Pressing it early is treated as
          "where do I type" and puts the caret in the first box the visitor has yet to fill, rather
          than spending a code attempt on a short one.
        */}
        <Button
          size="lg"
          className="h-[45px] w-full font-bold"
          disabled={emailVerified || isDisabled}
          loading={loadingverify}
          onClick={() => {
            if (!codeComplete) {
              inputRefs.current[code.findIndex((digit) => !digit)]?.focus();
              return;
            }
            validateTheCode(code.join(''));
          }}
        >
          Verify
        </Button>
        {resendCooldown > 0 ? (
          // Not a button while it counts down: there is nothing to press. Figma 8306-15353 puts a
          // clock beside it so the wait reads as a wait rather than as a control that broke.
          <p className="preflight preflight-p flex items-center justify-center gap-[5px] text-base leading-6 text-stone-400">
            <ClockIcon className="size-5 shrink-0" />
            {`Resend code in ${formatCooldown(resendCooldown)}`}
          </p>
        ) : (
          <button
            type="button"
            disabled={loadingverify || loadingResend || emailVerified}
            onClick={resendhandler}
            className="preflight preflight-button text-base leading-6 text-[#292524] underline hover:no-underline disabled:opacity-50"
          >
            Resend code
          </button>
        )}
        {onUseDifferentEmail && (
          <button
            type="button"
            // Not while a code is being checked. This starts a logout, and the verification already
            // in flight can still report success into a flow the visitor has just asked to leave —
            // two transitions racing over one decision. The wait is a second at most.
            disabled={loadingverify}
            onClick={onUseDifferentEmail}
            className="preflight preflight-button text-base leading-6 text-stone-500 underline hover:no-underline disabled:opacity-50"
          >
            Use a different email
          </button>
        )}
      </div>
    </>
  );
};

export default connect(({ user: { email: emailFromGlobalsState } }) => ({
  emailFromGlobalsState,
}))(VerificationCodeInput);
